import { Injectable } from '@angular/core';

import { BetBuilderOption, BetBuilderOptionMarket, Detailed } from '@cds/betting-offer/bet-builder';
import { DisplayType, MarketGroup, MarketGroupConfig, MarketGroupItem } from '@cds/betting-offer/grouping/bet-builder';
import { MarketTabInfo } from '@cds/betting-offer/grouping/fixture-view';
import { DetailedGroupingFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    EventOption,
    EventOptionGroup,
    EventOptionGroupType,
    EventOptionGrouping,
    ExtendedDisplayType,
} from '@frontend/sports/betting-offer/feature/model';
import { BetBuilderConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { hashCode } from '@frontend/sports/common/core/utils/string';
import { ScorecastLongIdPrefixes } from '@frontend/sports/types/components/bet-builder';

@Injectable({
    providedIn: 'root',
})
export class MarketBuilderFactory {
    private readonly logger: SportsRemoteLogger;
    raceText: { [key: string]: string };

    constructor(
        private detailedGroupingFactory: DetailedGroupingFactory,
        loggerFactory: LoggerFactory,
        private sitecore: Sitecore,
        private betBuilderConfig: BetBuilderConfig,
    ) {
        this.logger = loggerFactory.getLogger('MarketBuilderFactory');
        this.raceText = sitecore.betBuilderEntainUI?.children?.racetotext.messages ?? {};
    }

    getOptionGroups(
        config: MarketGroupConfig,
        betBuilderOptionMarkets: BetBuilderOptionMarket[],
        marketGroup: MarketGroup,
        sportcastId: number,
        sportId: number,
    ): EventOptionGroup[] {
        const optionGroups: EventOptionGroup[] = [];

        betBuilderOptionMarkets.forEach((sportcastMarket) => {
            const optionGroup = new EventOptionGroup();

            const detailed = sportcastMarket.detailedGrouping[0];

            const marketGroupItem = marketGroup.marketGroupItems.find((mgi) => mgi.id === detailed.marketGroupItemId);
            if (!marketGroupItem) {
                this.logger.warning(`BetBuilderMarket ${sportcastMarket.label}-${sportcastMarket.labelId} does not have a matching marketGroupItem`);

                return;
            }

            const optionGrouping = <EventOptionGrouping>{
                marketSet: 1,
                name: sportcastMarket.label,
                marketOrder: marketGroup.marketGroupItems.findIndex((mgi) => mgi.id === detailed.marketGroupItemId),
                marketHelpPath: marketGroupItem.helpPath,
                marketTabId: detailed.marketTabId,
                optionColumnId: detailed.optionColumnId,
                displayType: this.mapDisplayType(marketGroupItem.displayType),
                marketGroupId: detailed.marketGroupId,
                marketGroupItemId: detailed.marketGroupItemId,
                periodGroup: this.getPeriodGroup(config.marketTabs, detailed.marketTabId),
            };

            switch (marketGroupItem.displayType) {
                case DisplayType.SixPack:
                    optionGrouping.marketGroup = 0;
                    optionGrouping.name = marketGroupItem.name;
                    break;
                case DisplayType.OverUnder:
                case DisplayType.CorrectScore:
                case DisplayType.Spread:
                case DisplayType.RaceTo:
                default:
                    optionGrouping.marketGroup = 0;
            }

            optionGroup.id = sportcastMarket.labelId.toString();
            optionGroup.detailedGrouping = optionGrouping;
            optionGroup.name = marketGroupItem.name || sportcastMarket.label;
            optionGroup.type = EventOptionGroupType.Regular;
            optionGroup.hidden = false;
            optionGroup.winning = false;
            optionGroup.online = true;
            optionGroup.options = this.mapOptions(sportcastMarket, marketGroupItem, detailed, config, sportcastId, sportId);
            optionGroup.periodName = this.getPeriodName(config.marketTabs, optionGrouping.marketTabId);
            optionGroup.isMultipleSelection = sportcastMarket.isMultipleSelection;
            optionGroups.push(optionGroup);
        });

        const grouped = this.detailedGroupingFactory.groupMarkets(optionGroups);

        return grouped;
    }

    private mapOptions(
        sportcastMarket: BetBuilderOptionMarket,
        marketGroupItem: MarketGroupItem,
        detailed: Detailed,
        config: MarketGroupConfig,
        sportcastId: number,
        sportId: number,
    ): EventOption[] {
        const options: EventOption[] = [];

        sportcastMarket.options.forEach((opt, index) => {
            const option = new EventOption();
            option.id = this.getOptionId(opt, sportcastId);
            option.name = opt.value;
            option.online = true;
            option.longId = opt.globalIdLong;
            option.optionGroupId = sportcastMarket.labelId;
            option.shortId = opt.globalIdShort;
            option.optionColumnId = detailed.optionColumnId;
            option.order = opt.order;
            option.optionColumnName = this.getOptionColumnName(config.optionColumns, detailed.optionColumnId);
            option.description = opt.description;

            const parameterDisplayTypes = [
                DisplayType.CorrectScore,
                DisplayType.OverUnder,
                DisplayType.RaceTo,
                DisplayType.Spread,
                DisplayType.GoalScorer,
                DisplayType.YesNo,
                DisplayType.TeamPlayerProps,
            ];

            if (parameterDisplayTypes.includes(marketGroupItem.displayType) && !sportcastMarket.parameters?.length) {
                this.logger.warning(`${marketGroupItem.displayType} market: ${sportcastMarket.labelId} is missing custom parameters`);

                return;
            }

            if (marketGroupItem.displayType === DisplayType.TeamPlayerProps && sportcastMarket.parameters.length < 2) {
                this.logger.warning(`${DisplayType.TeamPlayerProps} market: ${sportcastMarket.labelId} should have multiple custom parameters`);

                return;
            }

            switch (marketGroupItem.displayType) {
                case DisplayType.CorrectScore:
                    option.marketAttribute = sportcastMarket.parameters[0].value;
                    break;
                case DisplayType.OverUnder:
                    option.marketAttribute = opt.value;
                    const overUnderParam =
                        sportcastMarket.parameters.length > 1 ? sportcastMarket.parameters[1].value : sportcastMarket.parameters[0].value;
                    option.name = overUnderParam;
                    break;
                case DisplayType.Spread:
                    option.optionGroupId = Math.abs(hashCode(opt.value));
                    option.optionAttribute = sportcastMarket.parameters[0].value;
                    option.marketAttribute = opt.value;
                    option.name = '';
                    break;
                case DisplayType.RaceTo:
                    option.marketAttribute = `${opt.value} ${this.raceText[sportId]}`;
                    option.name = sportcastMarket.parameters[0].value;
                    break;
                case DisplayType.YesNo:
                    option.optionColumnId = Math.abs(hashCode(opt.value));
                    option.optionColumnName = opt.value;
                    option.name = opt.value;
                    option.optionAttribute = sportcastMarket.parameters[0].value;
                    option.overridenName = sportcastMarket.parameters[0].value;
                    break;
                case DisplayType.GoalScorer:
                    if (!option.optionColumnName) {
                        this.logger.warning(`Option column not set for Goalscorer option`);
                    }
                    option.name = option.optionColumnName || opt.value;
                    // goalscorer columns are ordered by id
                    option.optionColumnId = option.order;
                    option.optionAttribute = sportcastMarket.parameters[0].value;
                    option.overridenName = sportcastMarket.parameters[0].value;
                    break;
                case DisplayType.TeamPlayerProps:
                    option.name = sportcastMarket.parameters[1].value;
                    option.marketAttribute = opt.value;
                    option.optionAttribute = `${sportcastMarket.parameters[0].value}-${opt.value}`;
                    option.optionColumnId = Math.abs(hashCode(sportcastMarket.parameters[1].value));
                    option.optionColumnName = sportcastMarket.parameters[1].value;
                    option.overridenName = sportcastMarket.parameters[0].value;
                    break;
                case DisplayType.SixPack:
                    const { overUnder, spread, matchWinner } = this.shouldOverrideSixPack(opt);

                    const shouldOverride = overUnder || spread || matchWinner;
                    if (!shouldOverride) {
                        break;
                    }

                    // Example mappings
                    // MatchWinner
                    // { globalIdLong: 'MatchWinner_Home', value: 'Orioles' } -> { name: '', marketAttribute: 'Orioles' }
                    // Spread
                    // { globalIdLong: 'Handicap_TotalRuns_Home_1.5', value: 'Orioles -1.5' } -> { name: 'Orioles', marketAttribute: '-1.5' }
                    // { globalIdLong: 'Handicap_TotalRuns_Home_1.5', value: '-1.5', parameters: [{ value: 'Orioles' }] } -> { name: 'Orioles', marketAttribute: '-1.5' }
                    // OverUnder
                    // { globalIdLong: 'MatchOverUnder_TotalRuns_Over_8.5', value: 'Over 8.5' } -> { name: '', marketAttribute: 'O 8.5' }
                    // { globalIdLong: 'MatchOverUnder_TotalRuns_Under_8.5', value: 'Under 8.5' } -> { name: '', marketAttribute: 'U 8.5' }

                    const optionValueSplit = opt.value.trim().split(/\s+/);
                    const optionAttribute = matchWinner ? opt.value : optionValueSplit.pop();
                    const totalsPrefix = index === 0 ? this.sitecore.eventGrid.SixPack_TotalsOver : this.sitecore.eventGrid.SixPack_TotalsUnder;

                    option.name = spread ? optionValueSplit.join(' ') || sportcastMarket.parameters[0]?.value : '';
                    option.marketAttribute = overUnder ? `${totalsPrefix} ${optionAttribute}` : optionAttribute;
            }

            options.push(option);
        });

        return options;
    }

    private getPeriodName(marketTabs: MarketTabInfo[], tabId: number | undefined): string | undefined {
        return marketTabs.find((oc) => oc.id === tabId)?.name;
    }

    private getPeriodGroup(marketTabs: MarketTabInfo[], tabId: number | undefined): number | undefined {
        return marketTabs.find((oc) => oc.id === tabId)?.sortOrder;
    }

    private getOptionColumnName(optionColumns: MarketTabInfo[], optionColumnId: number | undefined): string | undefined {
        return optionColumns.find((oc) => oc.id === optionColumnId)?.name;
    }

    private getOptionId(option: BetBuilderOption, sportcastId: number): number {
        return Math.abs(hashCode(option.globalIdLong || option.globalIdShort) + sportcastId);
    }

    private mapDisplayType(displayType: DisplayType): ExtendedDisplayType {
        switch (displayType) {
            case DisplayType.Standard:
                return ExtendedDisplayType.BetBuilderStandard;
            case DisplayType.SixPack:
                return ExtendedDisplayType.BetBuilderSixPack;
            case DisplayType.OverUnder:
                return ExtendedDisplayType.BetBuilderOverUnder;
            case DisplayType.CorrectScore:
                return ExtendedDisplayType.BetBuilderCorrectScore;
            case DisplayType.Spread:
                return ExtendedDisplayType.BetBuilderSpread;
            case DisplayType.RaceTo:
                return ExtendedDisplayType.BetBuilderRaceTo;
            case DisplayType.YesNo:
                return ExtendedDisplayType.BetBuilderYesNo;
            case DisplayType.GoalScorer:
                return ExtendedDisplayType.BetBuilderGoalScorer;
            case DisplayType.TeamPlayerProps:
                return ExtendedDisplayType.BetBuilderPlayerProps;
            default:
                return ExtendedDisplayType.BetBuilderStandard;
        }
    }

    private shouldOverrideSixPack(opt: BetBuilderOption): { [K in keyof ScorecastLongIdPrefixes]?: boolean } {
        const longId = opt.globalIdLong.toLowerCase();
        const longIdPrefixes = this.betBuilderConfig.scorecastLongIdPrefixes;

        const optionToOverride: { [K in keyof ScorecastLongIdPrefixes]?: boolean } = {};
        if (longIdPrefixes) {
            (Object.keys(longIdPrefixes) as (keyof ScorecastLongIdPrefixes)[]).find(
                (p) => (optionToOverride[p] = longId.startsWith(longIdPrefixes[p].toLowerCase())),
            );
        }

        return optionToOverride;
    }
}
